import { useState, useMemo } from 'react';

const MAX_INDICATORS = 5;

/**
 * Browseable catalog of Census ACS and CDC PLACES indicators.
 * Controlled component — parent (DashboardSettings) owns draft state and catalog data.
 *
 * Props:
 *   draft         - current array of selected indicator objects
 *   onDraftChange - state setter (accepts updater fn or value)
 *   census        - Census catalog items from useCatalog
 *   cdc           - CDC catalog items from useCatalog
 *   catalogLoading - boolean
 *   censusError   - string | null (shown only on Census tab)
 *   cdcError      - string | null (shown only on CDC tab)
 */
export default function OutcomesTab({
  draft,
  onDraftChange,
  census,
  cdc,
  svi,
  ejscreen,
  education,
  catalogLoading,
  censusError,
  cdcError
}) {
  const [activeSource, setActiveSource] = useState('census');
  const [search, setSearch] = useState('');

  const atCap = draft.length >= MAX_INDICATORS;

  function isSelected(id) {
    return draft.some(d => d.id === id);
  }

  function handleAdd(item) {
    if (!atCap && !isSelected(item.id) && item.supported) {
      onDraftChange(prev => [...prev, item]);
    }
  }

  function handleRemove(id) {
    onDraftChange(prev => prev.filter(d => d.id !== id));
  }

  const activeError = activeSource === 'census' ? censusError : activeSource === 'cdc' ? cdcError : null;

  const filtered = useMemo(() => {
    const sourceItems = { census, cdc, svi: svi ?? [], ejscreen: ejscreen ?? [], education: education ?? [] };
    const items = sourceItems[activeSource] ?? [];
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(
      item =>
        item.label.toLowerCase().includes(q) ||
        item.topic.toLowerCase().includes(q) ||
        (item.code || '').toLowerCase().includes(q)
    );
  }, [census, cdc, svi, ejscreen, education, activeSource, search]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach(item => {
      if (!map.has(item.topic)) map.set(item.topic, []);
      map.get(item.topic).push(item);
    });
    return [...map.entries()].map(([topic, indicators]) => ({ topic, indicators }));
  }, [filtered]);

  return (
    <div className="outcomes-tab">
      <div className="outcomes-tab-controls">
        <div className="outcomes-tab-subheader">
          <span className="outcome-selector-count">{draft.length} of {MAX_INDICATORS} selected</span>
          {atCap && (
            <span className="outcomes-tab-cap-hint">Remove an outcome to add a different one</span>
          )}
        </div>
        <div className="outcome-selector-tabs">
          {[
            { key: 'census',    label: 'Census ACS' },
            { key: 'cdc',       label: 'CDC Places' },
            { key: 'svi',       label: 'SVI' },
            { key: 'ejscreen',  label: 'EJSCREEN' },
            { key: 'education', label: 'Education' },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`outcome-selector-tab${activeSource === key ? ' outcome-selector-tab--active' : ''}`}
              onClick={() => { setActiveSource(key); setSearch(''); }}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="outcome-selector-search-row">
          <input
            className="outcome-selector-search"
            type="search"
            placeholder="Search by name, topic, or code…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="outcomes-tab-body">
        {/* Selected outcomes — always visible regardless of Census/CDC tab */}
        {draft.length > 0 && (
          <div className="outcomes-selected-section">
            <div className="outcome-selector-group-label">Selected</div>
            {draft.map(item => (
              <div key={item.id} className="outcome-selector-item outcome-selector-item--selected">
                <span className="outcome-selector-item-label">
                  <span className="outcome-selector-check" aria-hidden="true">✓</span>
                  {item.label}
                  {item.code && <code className="outcome-selector-code">{item.code}</code>}
                </span>
                <button
                  className="outcome-selector-btn outcome-selector-btn--remove"
                  onClick={() => handleRemove(item.id)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {catalogLoading && <div className="outcome-selector-loading">Loading catalog…</div>}
        {activeError && !catalogLoading && (
          <div className="outcome-selector-error">{activeError}</div>
        )}
        {!catalogLoading && grouped.length === 0 && (
          <div className="outcome-selector-empty">No outcomes match your search.</div>
        )}
        {!catalogLoading && grouped.map(({ topic, indicators }) => (
          <div key={topic} className="outcome-selector-group">
            <div className="outcome-selector-group-label">{topic}</div>
            {indicators.map(item => {
              const selected = isSelected(item.id);
              const addDisabled = !selected && (atCap || !item.supported);
              return (
                <div
                  key={item.id}
                  className={`outcome-selector-item${selected ? ' outcome-selector-item--selected' : ''}${!item.supported ? ' outcome-selector-item--unsupported' : ''}`}
                >
                  <span className="outcome-selector-item-label">
                    {selected && <span className="outcome-selector-check" aria-hidden="true">✓</span>}
                    {item.label}
                    {item.code && (
                      <code className="outcome-selector-code">{item.code}</code>
                    )}
                  </span>
                  {selected ? (
                    <button
                      className="outcome-selector-btn outcome-selector-btn--remove"
                      onClick={() => handleRemove(item.id)}
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      className="outcome-selector-btn outcome-selector-btn--add"
                      onClick={() => handleAdd(item)}
                      disabled={addDisabled}
                      title={
                        !item.supported
                          ? 'Variable mapping not configured'
                          : atCap
                          ? 'Remove an outcome first'
                          : undefined
                      }
                    >
                      + Add
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
